
import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Map, ExternalLink, Loader2, AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface ExternalMapWidgetProps {
  url: string;
  title?: string;
}

/**
 * Компонент для отображения внешней карты в iframe
 */
export const ExternalMapWidget: React.FC<ExternalMapWidgetProps> = ({ 
  url, 
  title = 'Карта курьеров и велосипедов' 
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Проверка протокола URL
  const isSecureUrl = url.startsWith('https://');
  const currentProtocol = window.location.protocol;

  const handleIframeLoad = () => {
    console.log('Внешняя карта загружена успешно');
    setLoading(false);
    setError(null);
  };

  const handleIframeError = (event: any) => {
    console.error('Ошибка при загрузке внешней карты:', event);
    setLoading(false);
    
    // Определяем тип ошибки
    let errorMessage = 'Не удалось загрузить карту.';
    
    if (!isSecureUrl && currentProtocol === 'https:') {
      errorMessage = 'Карта использует HTTP протокол, но сайт работает через HTTPS. Это может блокироваться браузером.';
    } else if (retryCount === 0) {
      errorMessage = 'Возможно, сайт карты блокирует отображение в iframe или недоступен.';
    } else {
      errorMessage = 'Не удалось загрузить карту после нескольких попыток.';
    }
    
    setError(errorMessage);
  };

  const openExternalMap = () => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const retryLoad = () => {
    console.log('Повторная попытка загрузки карты');
    setLoading(true);
    setError(null);
    setRetryCount(prev => prev + 1);
    
    // Перезагружаем iframe
    if (iframeRef.current) {
      iframeRef.current.src = '';
      setTimeout(() => {
        if (iframeRef.current) {
          iframeRef.current.src = url;
        }
      }, 100);
    }
  };

  const resetWidget = () => {
    setLoading(true);
    setError(null);
    setRetryCount(0);
    if (iframeRef.current) {
      iframeRef.current.src = url;
    }
  };

  return (
    <Card className="border-border/60 shadow-md bg-card/70 backdrop-blur-sm h-[700px] max-h-[80vh]">
      <CardHeader className="bg-card/50 flex flex-row items-center justify-between border-b border-border/30 py-3">
        <CardTitle className="text-lg font-semibold flex items-center">
          <Map className="h-5 w-5 mr-2 text-tuktuk-teal" />
          {title}
        </CardTitle>
        <div className="flex items-center space-x-2">
          {error && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 w-8 p-0" 
              onClick={retryLoad}
              title="Повторить загрузку"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          )}
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-8 w-8 p-0" 
            onClick={openExternalMap}
            title="Открыть в новом окне"
          >
            <ExternalLink className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0 overflow-hidden relative h-[calc(100%-60px)]">
        {/* Предупреждение о протоколе */}
        {!isSecureUrl && currentProtocol === 'https:' && (
          <Alert className="m-4 mb-0">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Внимание: Карта использует HTTP протокол, что может вызывать проблемы с загрузкой.
            </AlertDescription>
          </Alert>
        )}

        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/50 backdrop-blur-sm z-10">
            <div className="flex flex-col items-center space-y-2">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">
                Загрузка карты{retryCount > 0 ? ` (попытка ${retryCount + 1})` : ''}...
              </p>
            </div>
          </div>
        )}
        
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/50 backdrop-blur-sm z-10">
            <div className="flex flex-col items-center space-y-4 max-w-md text-center px-4">
              <div className="rounded-full bg-destructive/10 p-3">
                <Map className="h-6 w-6 text-destructive" />
              </div>
              <div className="space-y-2">
                <p className="text-destructive font-medium">{error}</p>
                <p className="text-sm text-muted-foreground">
                  Возможные причины: X-Frame-Options, CSP политики, или CORS ограничения сайта карты.
                </p>
              </div>
              <div className="flex flex-col space-y-2 w-full">
                <Button onClick={openExternalMap} className="w-full">
                  Открыть карту в новом окне
                </Button>
                <div className="flex space-x-2">
                  <Button variant="outline" onClick={retryLoad} className="flex-1">
                    Повторить
                  </Button>
                  <Button variant="outline" onClick={resetWidget} className="flex-1">
                    Сбросить
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <iframe 
          ref={iframeRef}
          src={url}
          width="100%"
          height="100%"
          style={{ 
            border: 'none', 
            borderRadius: '8px', 
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            minHeight: '640px'
          }}
          title="Карта велосипедов ТУК-ТУК"
          loading="lazy"
          allow="geolocation"
          onLoad={handleIframeLoad}
          onError={handleIframeError}
        />
      </CardContent>
    </Card>
  );
};
